import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button } from '../../../components/ui';
import { Section } from '../../../components/layout/AppShell';
import { cn } from '../../../lib/cn';

export default function TeacherSessionsShow({ session, activity, students, join_url }) {
  const [copied, setCopied] = useState(false);

  const copyCode = async () => {
    await navigator.clipboard.writeText(session.join_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyUrl = async () => {
    await navigator.clipboard.writeText(join_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleActivate = () => {
    router.post(`/teacher/sessions/${session.id}/activate`);
  };

  const handleComplete = () => {
    if (confirm('End this session? Students will no longer be able to join.')) {
      router.post(`/teacher/sessions/${session.id}/complete`);
    }
  };

  const statusColors = {
    draft: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    completed: 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400',
  };

  return (
    <TeacherShell scrollTitle={<><Link href="/teacher/dashboard" className="hover:text-blue-600 dark:hover:text-blue-400">Dashboard</Link>{'  ›  '}{activity.title}</>}>
      <TeacherPageHeader
        title={activity.title}
        subtitle="Session Management"
        backLink="/teacher/dashboard"
        backLabel="Back to dashboard"
        actions={
          <div className="flex items-center gap-3">
            <a href={`/teacher/sessions/${session.id}/pdf`} download>
              <Button variant="secondary">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  PDF
                </span>
              </Button>
            </a>
            <Link href={`/teacher/activities/${activity.id}/edit`}>
              <Button variant="secondary">
                Edit Activity
              </Button>
            </Link>
            {session.can_activate && (
              <Button variant="primary" onClick={handleActivate}>
                Start Session
              </Button>
            )}
            {session.can_complete && (
              <Button variant="danger" onClick={handleComplete}>
                End Session
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Join Code Card */}
          <Card className="p-8 text-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Join Code</p>
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className="text-5xl font-mono font-bold tracking-widest text-gray-900 dark:text-white">
                {session.join_code}
              </span>
              <button
                onClick={copyCode}
                className="p-2 rounded-lg hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors"
                title="Copy code"
              >
                {copied ? (
                  <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                  </svg>
                )}
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Share this code with your students to let them join
            </p>
            <div className="flex items-center justify-center gap-3">
              <Button variant="secondary" size="sm" onClick={copyUrl}>
                Copy Join Link
              </Button>
            </div>
          </Card>

          {/* Session Status */}
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Session Status</p>
                <span className={cn(
                  'inline-block px-3 py-1 text-sm font-medium rounded-full',
                  statusColors[session.status] || statusColors.draft
                )}>
                  {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                </span>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Questions</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{activity.question_count}</p>
              </div>
            </div>

            {session.status === 'active' && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <Link
                  href={`/teacher/sessions/${session.id}/map`}
                  className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  View Live Map
                </Link>
              </div>
            )}

            {session.started_at && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Started {new Date(session.started_at).toLocaleString()}
                </p>
              </div>
            )}
          </Card>

          {/* Instructions */}
          <Card className="p-6 bg-blue-50 border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
            <h3 className="font-medium text-blue-900 dark:text-blue-300 mb-3">How it works</h3>
            <ol className="text-sm text-blue-800 dark:text-blue-400 space-y-2 list-decimal list-inside">
              <li>Share the join code with your students</li>
              <li>Students enter the code at the join page</li>
              <li>They'll see the stimulus and complete the questions</li>
              <li>Monitor progress here as students work</li>
              <li>End the session when everyone is done</li>
            </ol>
          </Card>
        </div>

        {/* Sidebar - Students */}
        <div>
          <Card className="p-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Students ({students.length})
            </h3>

            {students.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                No students have joined yet.
                <br />
                Share the join code to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {students.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-800"
                  >
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center text-sm font-medium text-gray-600 dark:text-gray-300">
                      {student.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 dark:text-white truncate">
                        {student.full_name}
                      </p>
                      {student.joined_at && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Joined {new Date(student.joined_at).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </TeacherShell>
  );
}
